import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatConfirmDialogComponent } from 'src/app/shared/mat-confirm-dialog/mat-confirm-dialog.component';
import { ICategorie } from 'src/app/shared/models/categorie';

import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { CategorieService } from '../categorie-service';

@Component({
  selector: 'app-categorie-dialog',
  templateUrl: './categorie-dialog.component.html',
  styleUrls: ['./categorie-dialog.component.less']
})
export class CategorieDialogComponent implements OnInit {

  form!: UntypedFormGroup;
  isEditMode: boolean;
  isSubmitting = false;
  backendError: string | null = null;

  constructor(
    private _fb: UntypedFormBuilder,
    private categorieService: CategorieService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    public dialogRef: MatDialogRef<CategorieDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ICategorie | null
  ) {
    this.isEditMode = data != null;
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.form = this._fb.group({
      nom: [this.data?.nom ?? '', [Validators.required, Validators.maxLength(150)]]
    });
  }

  // ── Soumission ────────────────────────────────────────────────────────────

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.backendError = null;
    this.isSubmitting = true;

    const { nom } = this.form.value;

    const action$ = this.isEditMode
      ? this.categorieService.update(this.data!.id, { nom })
      : this.categorieService.create({ nom });

    const successMsg = this.isEditMode ? 'Catégorie modifiée avec succès' : 'Catégorie créée avec succès';
    const fallbackMsg = this.isEditMode ? 'Erreur lors de la modification' : 'Erreur lors de la création';

    action$.subscribe({
      next: (result) => {
        this.isSubmitting = false;
        this.snackbarService.openSnackBar(successMsg);
        this.dialogRef.close(result);
      },
      error: (err) => {
        this.isSubmitting = false;
        this.backendError = this.extractErrorMessage(err, fallbackMsg);
      }
    });
  }

  // ── Suppression ───────────────────────────────────────────────────────────

  onDelete(): void {
    const confirmRef = this.dialog.open(MatConfirmDialogComponent, {
      width: '400px',
      data: { message: `Supprimer la catégorie "${this.data!.nom}" ?` }
    });

    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.isSubmitting = true;
      this.categorieService.delete(this.data!.id).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.snackbarService.openSnackBar('Catégorie supprimée avec succès');
          this.dialogRef.close('deleted');
        },
        error: (err) => {
          this.isSubmitting = false;
          this.backendError = this.extractErrorMessage(err, 'Erreur lors de la suppression');
        }
      });
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  // ── Helper erreurs ────────────────────────────────────────────────────────

  private extractErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (body?.message && typeof body.message === 'string') return body.message;
    if (typeof body === 'string') return body;
    return fallback;
  }
}